import React, { useState, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { ScaleTime, ScaleLinear } from 'd3-scale'; //from DefinitelyTyped types
import { Line } from 'd3-shape'; //from DefinitelyTyped types
import CookieConsent from "react-cookie-consent";
import { 
	wlProgressDataMemberType, 
	wlProgressData, 
	croppedProgPictureFilenames
} from './GlobalData';
import { getNewXIndex } from './Common';
import ImageSet from './ImageSet';
import ControlPanel from './ControlPanel';
import HelpInfoOverlay from './HelpInfoOverlay';
import './ProgPicsViewer.css';


//const parseTime = d3.timeParse("%Y-%m-%d");

type wlProgressDataWithMetadataMemberType = wlProgressDataMemberType & {
  indexRef: number;
	imageRef: string;
};

const croppedProgPicFileDirectory = "./progress_pics_cropped/";

const margin = { top: 5, right: 15, bottom: 15, left: 30 };
const defaultChartDimensions = { width: 900, height: 250 };
const smallScreenBreakpoint = 700;

const wlProgressDataWithMetadata = wlProgressData.map( (data, i) => { 
  let retVal: wlProgressDataWithMetadataMemberType = {
		date: data.date,
		weight: data.weight,
		cheatDay: data.cheatDay,
		indexRef: i,
		imageRef: data.date.toISOString().replace(/-/g,'').substring(0,8)
	};
  return retVal; 
});


let formatXAxis = (function() {
	let xFormatShort = d3.utcFormat('%b');
	let xFormatLong = d3.utcFormat('%b'); //redundant right now
	let prevYear = 0;
	return (d:any, screenSize: string) => {
		if(prevYear !== d.getUTCFullYear()) {
			//console.log('d=',d,'year=',d.getUTCFullYear(),'prevYear=',prevYear);
			prevYear = d.getUTCFullYear();
			return ( screenSize === 'sm' ? "'"+String(d.getUTCFullYear()).substring(2,4) : d.getUTCFullYear() );
		}
		return ( screenSize === 'sm' ? xFormatShort(d).substring(0,1) : xFormatLong(d) );
	}
})();

function ProgPicsViewer() {
  let [xIndex, setXIndex] = useState(0);
	let [playingState, setPlayingState] = useState(0);
	let [graphVisible, setGraphVisible] = useState(true);
  let [setContainerWidth, setSetContainerWidth] = useState(false);
  let [width, setWidth] = useState(900 - margin.left - margin.right);
  let [height, setHeight] = useState(250 - margin.top - margin.bottom);
	let [screenSize, setScreenSize] = useState('sm'); //sm,lg
	let [allowLooping, setAllowLooping] = useState(false);
  let resizeTimer = useRef(0);
  let keyPressTimer = useRef(0);
	let playingIntervalId = useRef(0);
	let graphContainer = useRef<HTMLDivElement>(null);
	let graphVisibleToggle = useRef<HTMLButtonElement>(null);
	let allowLoopingToggle = useRef<HTMLButtonElement>(null);
	let x:(ScaleTime<number, number, never>|any) = useRef();
	let y:(ScaleLinear<number, number, never>|any) = useRef();
	let line:(Line<wlProgressDataWithMetadataMemberType>|any) = useRef();

	let handleGraphContainerResize = () => {
		if(resizeTimer.current === 0) {
    	resizeTimer.current = window.setTimeout( () => {
      //console.log('set new timeout');
    	setSetContainerWidth(false);
    	resizeTimer.current = 0;
		}, 350)}
	}

	let handleScreenSizeChange = () => {
		setScreenSize(window.innerWidth >= smallScreenBreakpoint ? 'lg' : 'sm');
	}

  let handleXIndexUpdate = (curXIndex:number, newXIndex:number) => {    
		if(curXIndex !== newXIndex) {
			setXIndex(newXIndex ?? 0)
		}
  }

	useEffect( () => {
		//console.log('initial call to handleScreenSizeChange()');
		handleScreenSizeChange();
	}, []);

	useEffect( () => {
		let handleArrowKeyEvent = (event: KeyboardEvent) => {
			//console.log('keypress',event);
			if(keyPressTimer.current === 0) {
				//console.log('key press handled. key press timer set.');	
				if(event.key === "ArrowRight") {
						setXIndex( xIndex => getNewXIndex(xIndex,1, wlProgressDataWithMetadata.length, allowLooping))
				} else if (event.key === "ArrowLeft") {
						setXIndex( xIndex => getNewXIndex(xIndex,-1, wlProgressDataWithMetadata.length, allowLooping))
				}
				keyPressTimer.current = window.setTimeout( () => {
					//console.log('keypresstimer reset.');
					keyPressTimer.current = 0;
				}, 50);
			}			
		};
		//console.log('added keydown listener');
		window.addEventListener('keydown', handleArrowKeyEvent);
		return () => {
			//console.log('removed keydown listener');
			window.removeEventListener('keydown', handleArrowKeyEvent);
		}
	}, [allowLooping]);

  useEffect( () => {
		//console.log('added resize listeners');
    window.addEventListener('resize', handleGraphContainerResize);
    window.addEventListener('resize', handleScreenSizeChange);
    return () => {
			//console.log('removed resize listeners');
			window.removeEventListener("resize", handleGraphContainerResize);
			window.removeEventListener("resize", handleScreenSizeChange);
		}
	}, []);

	useEffect( () => {
		document.title = 'Weightloss Progress Pics Viewer';
	}, []);

	useEffect( () => {
		if(playingState !== 0) {
			clearInterval(playingIntervalId.current);
			//console.log('set interval');
			playingIntervalId.current = window.setInterval( () => {
				setXIndex( xIndex => getNewXIndex(xIndex, playingState, wlProgressDataWithMetadata.length, allowLooping));
			}, 200);
		} else {
			clearInterval(playingIntervalId.current);
			playingIntervalId.current = 0;
		}
	}, [playingState, allowLooping]);

	useEffect( () => {
		//stop playing if wrapping is not allowed and reached end or start of timeline
		if(
				!allowLooping && 
				( ( xIndex === wlProgressDataWithMetadata.length-1 && playingState === 1) 
				|| 
				( xIndex === 0  && playingState === -1) ) 
			) {
				setPlayingState(0);
		}
	}, [allowLooping, playingState, xIndex]);

	useEffect( () => {
		if(graphContainer.current instanceof HTMLElement) {
			if(graphVisible) {
				graphContainer.current.classList.add('graphContainerVisible')
				setSetContainerWidth(false);
			} else {
				graphContainer.current.classList.remove('graphContainerVisible');
			}
		}
	},[graphVisible]);

	useEffect( () => {
    if(!setContainerWidth) {
			//console.log('set container width');
			let {width: chartContainerWidth, height: chartContainerHeight} = document.getElementById("graphContainer")?.getBoundingClientRect() ?? {width: defaultChartDimensions.width, height: defaultChartDimensions.height};
			setWidth(chartContainerWidth - margin.left - margin.right);
			setHeight(chartContainerHeight - margin.top - margin.bottom);
			setSetContainerWidth(true);
		}
	}, [setContainerWidth]);

  useEffect( () => {
		//console.log('recalculate axes');
    //for typescript - possibly better way to handle undefined,undefined case
    const [minX=0, maxX=0] = d3.extent(wlProgressDataWithMetadata, d => d.date);
		//setup x axis timeScale
    x.current = d3.scaleUtc()
      .domain([minX, maxX])
      .range([margin.left-8, width - margin.right])
			.nice();
    
		//setup y axis linear scale
    y.current = d3.scaleLinear()
      .domain(
        [
          (d3.min(wlProgressDataWithMetadata, d => d.weight) ?? 0),
          (d3.max(wlProgressDataWithMetadata, d => d.weight) ?? 0)
        ]
      )
      .range([height - margin.bottom, margin.top])
			.nice();

    line.current = d3.line<wlProgressDataWithMetadataMemberType>()
      .x( (d) => x.current(d.date) )
      .y( (d) => y.current(d.weight) );
	}, [width, height]);
  
	useEffect( () => {
		//console.log('re-render graph');
		d3.select("#graphContainer").selectAll("*").remove();

    const svg = d3
      .select("#graphContainer")
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    svg
			.append("g")
			.attr("class","graphAxis")
			.attr("transform", `translate(0, ${height-margin.bottom+1})`)
			.attr("color","white")
			//.call(d3.axisBottom(x).ticks(undefined, '%b'));//d3.utcFormat('%b %d')));
			//.call(d3.axisBottom(x).ticks(undefined).tickFormat(formatXAxis));
			.call(d3.axisBottom(x.current).tickFormat((d) => formatXAxis(d, screenSize)));
			//.call(d3.axisBottom(x));

    svg
			.append("g")
			.attr("class","graphAxis")
			.attr("transform", `translate(10, 0)`)
			.attr("color","white")
			.call(d3.axisLeft(y.current));
    
		//build line 
    svg.append("path")
      //.data([wlProgressDataWithMetadata])
      .attr("d", line.current(wlProgressDataWithMetadata))
      .attr("fill", "none")
      .attr("stroke","white")
      .attr("stroke-width", () => screenSize === 'sm' ? '1': '1.5')
      .attr("stroke-miterlimit","1"); 

    //build circle markers
		svg.append("g").selectAll("circle")
    .data(wlProgressDataWithMetadata)
    .join("circle")
      .attr("fill", (d,i) => { 
				if(d.cheatDay) { return "darkRed"; }
				return "rgba(0,0,0,0)";
			})
      .attr("stroke", (d,i) => { 
				if(d.cheatDay) { return "white"; }
				return "rgba(0,0,0,0)";
			})
      .attr("stroke-width", () => screenSize === 'sm' ? '0': '0.5')
      //.attr("id", (d,i) => "circle_mark_"+d.indexRef)
      .attr("cx", d => x.current(d.date))
      .attr("cy", d => y.current(d.weight))
      .attr("r", (d,i) => { 
				if(d.cheatDay) { 
					return screenSize === 'sm' ? '1.25': '2'; 
				}
				return '2';
			});

    svg.append("g").selectAll("circle")
    	.data([wlProgressDataWithMetadata[xIndex]])
	    .join("circle")
      	.attr("fill", "none")
	      .attr("stroke", d => {
					return d.cheatDay ? "darkRed" : "#39e";
				})
	      .attr("stroke-width", 3)
	      .attr("cx", d => x.current(d.date))
	      .attr("cy", d => y.current(d.weight))
      	.attr("r", screenSize === 'sm' ? '5': '7');


	  svg.on('click', (event, d) => {
			//console.log('event=',event);
			let eventTargetIndexRef = event?.target?.__data__?.indexRef;
			if(event.target.localName === 'circle' && eventTargetIndexRef > -1) {
				//console.log('indexRef=',eventTargetIndexRef);
				handleXIndexUpdate(xIndex, eventTargetIndexRef ?? 0);
			}
		});

  });

	let formattedNotes = (wlProgressDataWithMetadata[xIndex].cheatDay ? 'Cheat day' : '');
  
	return (
    <div>
			<div className="overlayContainer">
				<ControlPanel
					xIndex={xIndex}
					xIndexMax={wlProgressDataWithMetadata.length}
					setXIndex={setXIndex}
					allowLooping={allowLooping}
					playingState={playingState}
					setPlayingState={setPlayingState} 
					screenSize={screenSize}
					currentDate={wlProgressDataWithMetadata[xIndex].date}
					currentWeight={wlProgressDataWithMetadata[xIndex].weight}
					currentNotes={formattedNotes}
				/>
      	<div ref={graphContainer} id="graphContainer" className="graphContainer graphContainerVisible"></div>
			</div>
			<ImageSet
				currentImageRef={wlProgressDataWithMetadata[xIndex].imageRef}
				basePath={croppedProgPicFileDirectory}
				images={croppedProgPictureFilenames}
			/>
			<HelpInfoOverlay 
				screenSize={screenSize}
			/>
			<div className="bottomLeftControlsContainer">
			<button 
				type="button"
				ref={graphVisibleToggle}
				className="graphVisibilityToggleButton"
				onClick={ () => {
					setGraphVisible( graphVisible => !graphVisible);
				}}
			>
				{ graphVisible ? "Hide Graph" : "Show Graph" }
			</button>
			<button 
				type="button"
				ref={allowLoopingToggle}
				className="allowLoopingToggleButton"
				onClick={ () => {
					setAllowLooping( allowLooping => !allowLooping);
				}}
			>
				{allowLooping ? "Loop ON" : "Loop OFF" }
			</button>
			
			</div>
			<CookieConsent 
				style={{"zIndex": "9000", "background": "rgba(5, 5, 5, 0.75)"}} 
				buttonStyle={{"background": "#68FDE8"}}
			>
				This website uses cookies to monitor web traffic.
			</CookieConsent>
    </div>
  );
}

export default ProgPicsViewer;
